import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Breadcrumb, Col, Row, Button, Alert, Spinner } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import API from "../Utility";
import validator from "validator";
import { Formik, Form, Field, ErrorMessage, useField } from "formik";
import * as Yup from "yup";

const usernameRegExp = /^\S*$/;
const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const phoneRegExp =
  /^((0|(\(0\)))?|(00|(\(00\)))?(\s?|-?)(27|\(27\))|((\+27))|(\(\+27\))|\(00(\s?|-?)27\))( |-)?(\(?0?\)?)( |-)?\(?(1[0-9]|2[1-4,7-9]|3[1-6,9]|4[0-9]|5[1,3,6-9]|7[1-4,6,8,9]|8[0-9])\)?(\s?|-?)((\d{3}(\s?|-?)\d{4}$)|((\d{4})(\s?|-?)(\d{3})$)|([0-2](\s?|-?)(\d{3}(\s?|-?)\d{3}$)))/;
const saIdRegExp =
  /(((\d{2}((0[13578]|1[02])(0[1-9]|[12]\d|3[01])|(0[13456789]|1[012])(0[1-9]|[12]\d|30)|02(0[1-9]|1\d|2[0-8])))|([02468][048]|[13579][26])0229))(( |-)(\d{4})( |-)([01]8((( |-)\d{1})|\d{1}))|(\d{4}[01]8\d{1}))/gm;

export default function UpsertUserView({ children, ...routeProps }) {
  let { project, userId } = useParams();
  let history = useHistory();
  const location = useLocation();
  const user = location && location.state ? location.state.user : {};
  const { action } = routeProps;

  const [formMode, setFormMode] = useState(action); // create or update handled here
  const [displayName, setDisplayName] = useState();

  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submittingForm, setSubmittingForm] = useState(false);

  const [userData, setUserData] = useState({ action: action });

  // Initialize dynamic form validation
  let dynamicFormValidation = {};
  let resetPasswordValidation = false;

  // Initialize dynamic form fields
  let dynamicFormFields = {};

  const setInputValidationByFieldType = (currentField) => {
    if (currentField.type === "text") {
      if (currentField.fieldName === "username") {
        dynamicFormValidation[currentField.fieldName] = Yup.string()
          .matches(usernameRegExp, currentField.fieldLabel + " is not valid")
          .max(250, "Must be 250 characters or less")
          .required(
            currentField.isRequired
              ? currentField.fieldLabel + " is required"
              : false
          );
      } else {
        dynamicFormValidation[currentField.fieldName] = Yup.string()
          .max(250, "Must be 250 characters or less")
          .required(
            currentField.isRequired
              ? currentField.fieldLabel + " is required"
              : false
          );
      }
    }
    if (currentField.type === "password") {
      dynamicFormValidation[currentField.fieldName] = Yup.lazy((value) => {
        if (value) {
          return Yup.string()
            .min(6, "Must be 6 characters or more")
            .required(
              currentField.isRequired
                ? currentField.fieldLabel + " is required"
                : false
            );
        }
        return Yup.string()
          .min(6, "Must be 6 characters or more")
          .required(
            currentField.isRequired
              ? currentField.fieldLabel + " is required"
              : false
          );
      });
    }
    if (currentField.type === "email" || currentField.type === "emailAddress") {
      dynamicFormValidation[currentField.fieldName] = Yup.string()
        .email(currentField.fieldLabel + " is not valid")
        .required(
          currentField.isRequired
            ? currentField.fieldLabel + " is required"
            : false
        );
    }
    if (currentField.type === "username") {
      dynamicFormValidation[currentField.fieldName] = Yup.string()
        .matches(usernameRegExp, currentField.fieldLabel + " is not valid")
        .max(250, "Must be 250 characters or less")
        .required(
          currentField.isRequired
            ? currentField.fieldLabel + " is required"
            : false
        );
    }
    if (currentField.type === "list") {
      let validOptions = currentField.options.map(function (option, key) {
        return option;
      });
      if (currentField.isRequired) {
        dynamicFormValidation[currentField.fieldName] = Yup.string()
          .oneOf(validOptions, currentField.fieldLabel + " is required")
          .required(
            currentField.isRequired
              ? currentField.fieldLabel + " is required"
              : false
          );
      } else {
        dynamicFormValidation[currentField.fieldName] = Yup.string();
      }
    }
    if (currentField.type === "telephone") {
      dynamicFormValidation[currentField.fieldName] = Yup.string()
        .matches(phoneRegExp, currentField.fieldLabel + " is not valid")
        .required(
          currentField.isRequired
            ? currentField.fieldLabel + " is required"
            : false
        );
    }
    if (currentField.type === "date") {
      dynamicFormValidation[currentField.fieldName] = Yup.date().required(
        currentField.isRequired
          ? currentField.fieldLabel + " is required"
          : false
      );
    }
    if (currentField.type === "saIdNumber") {
      dynamicFormValidation[currentField.fieldName] = Yup.string()
        .matches(saIdRegExp, currentField.fieldLabel + " is not valid")
        .required(
          currentField.isRequired
            ? currentField.fieldLabel + " is required"
            : false
        );
    }
  };

  const setInitialFormValues = (currentField, action) => {
    if (action && action === "create") {
      dynamicFormFields[currentField.fieldName] = "";
    } else if (
      action &&
      action === "update" &&
      user &&
      (user[currentField.fieldName] ||
        (user.providerData && user.providerData[currentField.fieldName]))
    ) {
      if (
        currentField.fieldName === "username" ||
        currentField.fieldName === "password"
      ) {
        dynamicFormFields[currentField.fieldName] =
          user[currentField.fieldName] || "";
      } else {
        dynamicFormFields[currentField.fieldName] =
          user.providerData[currentField.fieldName] || "";
      }
    } else {
      dynamicFormFields[currentField.fieldName] = "";
    }
  };

  const setInputType = (fieldType) => {
    if (fieldType === "password") {
      return "password";
    } else if (fieldType === "email") {
      return "email";
    } else if (fieldType === "date") {
      return "date";
    } else if (fieldType === "list") {
      return "select";
    } else if (fieldType === "telephone") {
      return "tel";
    } else {
      return "text";
    }
  };

  const setSelectOptions = (field) => {
    return (
      <>
        <option value={null}>Choose a {field.fieldLabel}</option>
        {field.options.map(function (item, index) {
          return (
            <option key={index} value={item}>
              {item}
            </option>
          );
        })}
      </>
    );
  };

  let orderedDataFields = user?.providerDataConfig?.filter((field) => {
    return (
      field.endUserPermissions !== "system" &&
      field.type !== "group" &&
      field.type !== "groups"
    );
  });

  // Generate dynamic form defaults
  if (orderedDataFields && orderedDataFields.length > 0) {
    for (let i = 0; i < orderedDataFields.length; i++) {
      let currentField = orderedDataFields[i];

      // Generate dynamic form validation
      setInputValidationByFieldType(currentField);

      // Generate dynamic form fields
      setInitialFormValues(currentField, action);
    }
  }

  const handleChange = (e) => {
    if (e && e.target && e.target.type && e.target.type === "password") {
      resetPasswordValidation = true;
    }
  };
  const alwaysReadonlyList = ["activationDate", "activationStatus"];
  function isAlwaysReadonly(fieldName) {
    return alwaysReadonlyList.findIndex((f) => f === fieldName) !== -1;
  }
  const renderFields = (inputs, touched, errors) => {
    if (inputs && inputs.length > 0) {
      return inputs.map((input, key) => {
        if (input.type === "list") {
          return (
            <div className="form-group" key={key}>
              <label htmlFor={input.fieldName}>
                {input.fieldLabel}
                {input.isRequired ? " *" : ""}
              </label>
              <Field
                as="select"
                name={input.fieldName}
                disabled={
                  submitSuccess && submitSuccess.length > 0 ? true : false
                }
                className={`form-control ${
                  touched[input.fieldName] && errors[input.fieldName]
                    ? "is-invalid"
                    : ""
                } ${
                  touched[input.fieldName] && !errors[input.fieldName]
                    ? "is-valid"
                    : ""
                }`}
              >
                {setSelectOptions(input)}
              </Field>
              <ErrorMessage
                component="div"
                name={input.fieldName}
                className="invalid-feedback"
              />
            </div>
          );
        } else {
          return (
            <div className="form-group" key={key}>
              <label htmlFor={input.fieldName}>
                {input.fieldLabel}
                {input.isRequired ? " *" : ""}
              </label>
              <Field
                type={setInputType(input.type)}
                name={input.fieldName}
                disabled={
                  isAlwaysReadonly(input.fieldName) ||
                  (submitSuccess && submitSuccess.length > 0 ? true : false)
                }
                onKeyUp={handleChange}
                placeholder={input.fieldLabel}
                className={`form-control ${
                  touched[input.fieldName] && errors[input.fieldName]
                    ? "is-invalid"
                    : ""
                } ${
                  touched[input.fieldName] && !errors[input.fieldName]
                    ? "is-valid"
                    : ""
                }`}
              />
              <ErrorMessage
                component="div"
                name={input.fieldName}
                className="invalid-feedback"
              />
            </div>
          );
        }
      });
    }
  };

  useEffect(() => {
    if (action === "update") {
      if (user && user.providerData) {
        // set user data
        setUserData({
          ...userData,
          ...dynamicFormFields,
          origin: user.providerData.origin,
        });

        setDisplayName(user.providerData.displayName);
      }
    }
  }, []);

  const redirectOnSuccess = () => {
    setTimeout(() => {
      // history.push({ pathname: `/campaigns/${project}/${campaignId}` });
      history.push({ pathname: `/users/${project}/${userId}` });
    }, 500);
  };

  const upsertUser = async (values) => {
    try {
      values.action = action;
      if (values.action === "create") {
        values.origin = "user management";
      }
      values._id = user._id;
      const apiCallConfig = {
        url: `/admin/${project}/users/${action}`,
        data: values,
      };
      setSubmittingForm(true);
      const response = await API.post(apiCallConfig);
      if (response) {
        if (response.user) {
          if (!userId && response.user._id) {
            userId = response.user._id;
          }
        }
        if (response.status === "updated" && action === "update") {
          // success
          setSubmitSuccess("User updated successfully");
          redirectOnSuccess();
        } else if (response.status === "updated" && action === "create") {
          // tried to create a new user but an update occurred instead, user exists
          setSubmitSuccess("User already exists and was updated");
          redirectOnSuccess();
        } else if (response.status === "created" && action === "create") {
          setSubmitSuccess("User created successfully");
          redirectOnSuccess();
        } else if (response.status === "created" && action === "update") {
          setSubmitSuccess("User not found but was created");
          redirectOnSuccess();
        } else {
          setSubmitError(response.message);
        }
        setSubmittingForm(false);
      }

    } catch (err) {
      if (err.response) {
        setSubmitError(err.response.data.message);
      } else {
        setSubmitError("An error occurred, please try again");
      }
      setSubmittingForm(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitSuccess(null);
    setSubmitError(null);
    setUserData({ ...values, action: action, origin: "user management" });
    await upsertUser(values);
    history.push({ pathname: `/users/${project}/${userId}` });
  };

  const handleCancel = (event) => {
    if (action === "update") {
      history.push({ pathname: `/users/${project}/${userId}` });
    } else {
      history.push({ pathname: `/users/${project}` });
    }
  };

  return (
    <React.Fragment>
      <Row>
        {/*This is where the warning banner will go*/}
        {formMode === "update" && !user.isUserControlled ? (
          <Col>
            <Alert variant={"warning"}>
              This record is linked to an external data source that may
              overwrite your changes
            </Alert>
          </Col>
        ) : null}
      </Row>
      <Row>
        <Col>
          <Breadcrumb as="nav" bsPrefix="breadcrumb ph-admin__breadcrumb">
            <LinkContainer to={`/users/${project}`}>
              <Breadcrumb.Item bsPrefix="breadcrumb-item clickable">
                Users
              </Breadcrumb.Item>
            </LinkContainer>
            <LinkContainer
              to={
                formMode === "create"
                  ? `users/${project}`
                  : `/users/${project}/${userId}`
              }
            >
              <Breadcrumb.Item active>
                {formMode === "create" ? "New User" : displayName}
              </Breadcrumb.Item>
            </LinkContainer>
          </Breadcrumb>
        </Col>
      </Row>

      <Row>
        <Col>
          <Formik
            initialValues={dynamicFormFields}
            validationSchema={Yup.object().shape(dynamicFormValidation)}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              handleSubmit(values);
            }}
          >
            {({ touched, errors, isSubmitting }) => (
              <Form noValidate>
                {renderFields(orderedDataFields, touched, errors)}

                {submitError && <Alert variant={"danger"}>{submitError}</Alert>}
                {submitSuccess && (
                  <Alert variant={"success"}>{submitSuccess}</Alert>
                )}

                <div className="form-group">
                  {submittingForm ? (
                    <Spinner animation="border" role="status">
                      <span className="sr-only">Loading...</span>
                    </Spinner>
                  ) : (
                    <>
                      <button
                        className={`mb-2 btn btn-outline-secondary ${
                          submittingForm ||
                          (submitSuccess && submitSuccess.length > 0)
                            ? "hide"
                            : ""
                        }`}
                        onClick={handleCancel}
                        style={{ marginRight: "5px" }}
                        disabled={
                          submittingForm ||
                          (submitSuccess && submitSuccess.length > 0
                            ? true
                            : false)
                        }
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`mb-2 btn btn-outline-primary ${
                          submittingForm ||
                          (submitSuccess && submitSuccess.length > 0)
                            ? "hide"
                            : ""
                        }`}
                        disabled={
                          submittingForm ||
                          (submitSuccess && submitSuccess.length > 0
                            ? true
                            : false)
                        }
                      >
                        Save
                      </button>
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </React.Fragment>
  );
}
